/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { User } from './user.model';

export interface IUserPermission {
  _id?: string;
  user?: string | User;
  brokerUserPermissions?: IUserPermissionType[];
  shipperUserPermissions?: IUserPermissionType[];
  carrierUserPermissions?: IUserPermissionType[];
  additionals?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface IUserPermissionType {
  name: string;
  value: string[];
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface IUiPermissionApiItem {
  featureName: string;
  permissionName: USER_PERMISSION_NAME;
  type: IUiPermissionApiItemType;
}

export enum IUiPermissionApiItemType {
  Page = 'Page',
  Tab = 'Tab',
  InnerTab = 'InnerTab',
}

export interface IUserPermissionGroup {
  _id?: string;
  tenantId?: string;
  title: string;
  description: string;
  brokerUserPermissions?: IUserPermissionType[];
  shipperUserPermissions?: IUserPermissionType[];
}

export enum AdditionalPermissions {
  EXPORT_REPORTS = 'Export Reports',
}

export enum USER_PERMISSION_VALUE {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  HIDDEN = 'HIDDEN',
  DISABLED = 'DISABLED',
}

export enum USER_PERMISSION_PLATFORM {
  ADMIN = 'Admin',
  SHIPPER_WEB = 'Shipper Web',
  SHIPPER_TMS = 'Shipper TMS',
  CARRIER_TMS = 'Carrier TMS',
}

export enum USER_PERMISSION_NAME {
  BROKER__SHIPMENTS_LISTING = 'BROKER__SHIPMENTS_LISTING',
  BROKER__SHIPMENTS_EDI_TENDER = 'BROKER__SHIPMENTS_EDI_TENDER',
  BROKER__APPOINTMENTS_LISTING = 'BROKER__APPOINTMENTS_LISTING',
  BROKER__INSTANT_SMART_PRICE = 'BROKER__INSTANT_SMART_PRICE',
  BROKER__WORKFORCE = 'BROKER__WORKFORCE',
  BROKER__MARKETPLACE = 'BROKER__MARKETPLACE',
  BROKER__PRICING_MANAGEMENT = 'BROKER__PRICING_MANAGEMENT',
  BROKER__CARRIERS_SEARCH = 'BROKER__CARRIERS_SEARCH',
  BROKER__CARRIERS_DATABASE = 'BROKER__CARRIERS_DATABASE',
  BROKER__CARRIERS_HISTORY = 'BROKER__CARRIERS_HISTORY',
  BROKER__CARRIERS_APPROVAL_REQUESTS = 'BROKER__CARRIERS_APPROVAL_REQUESTS',
  BROKER__LOAD_TENDER_ELIGIBILITY = 'BROKER__LOAD_TENDER_ELIGIBILITY',
  BROKER__CUSTOMERS_LISTING = 'BROKER__CUSTOMERS_LISTING',
  BROKER__LOCATIONS_LISTING = 'BROKER__LOCATIONS_LISTING',
  BROKER__ACCOUNTING = 'BROKER__ACCOUNTING',
  BROKER__QUOTE_BANK = 'BROKER__QUOTE_BANK',
  BROKER__REPORTING_DASHBOARD = 'BROKER__REPORTING_DASHBOARD',
  BROKER__REPORTING_SERVICE_REPORTS = 'BROKER__REPORTING_SERVICE_REPORTS',
  BROKER__REPORTING_SALES_REPORTS = 'BROKER__REPORTING_SALES_REPORTS',
  BROKER__REPORTING_SALES_GOALS = 'BROKER__REPORTING_SALES_GOALS',
  BROKER__REPORTING_OPERATOR_REPORTS = 'BROKER__REPORTING_OPERATOR_REPORTS',
  BROKER__REPORTING_OPERATOR_GOALS = 'BROKER__REPORTING_OPERATOR_GOALS',
  BROKER__REPORTING_QUOTE_BANK_REPORTS = 'BROKER__REPORTING_QUOTE_BANK_REPORTS',
  BROKER__REPORTING_WORKFORCE_REPORTS = 'BROKER__REPORTING_WORKFORCE_REPORTS',
  BROKER__REPORTING_WORKFORCE_GOALS = 'BROKER__REPORTING_WORKFORCE_GOALS',
  BROKER__REPORTING_SHIPMENT_REPORTS = 'BROKER__REPORTING_SHIPMENT_REPORTS',
  BROKER__REPORTING_LANES_REPORTS = 'BROKER__REPORTING_LANES_REPORTS',

  // Accounting
  BROKER__ACCOUNTING__CREDIT_APPLICATION = 'BROKER__ACCOUNTING__CREDIT_APPLICATION',
  BROKER__ACCOUNTING__ACC_PAYABLE = 'BROKER__ACCOUNTING__ACC_PAYABLE',
  BROKER__ACCOUNTING__ACC_RECEIVABLE = 'BROKER__ACCOUNTING__ACC_RECEIVABLE',
  BROKER__ACCOUNTING__INVOICES = 'BROKER__ACCOUNTING__INVOICES',
  BROKER__ACCOUNTING__BILLS = 'BROKER__ACCOUNTING__BILLS',
  BROKER__ACCOUNTING__QUICKBOOKS = 'BROKER__ACCOUNTING__QUICKBOOKS',
  BROKER__ACCOUNTING__NETSUITE = 'BROKER__ACCOUNTING__NETSUITE',
  BROKER__ACCOUNTING__SETUP = 'BROKER__ACCOUNTING__SETUP',
  BROKER__ACCOUNTING__ACC_AGING_REPORT = 'BROKER__ACCOUNTING__ACC_AGING_REPORT',
  BROKER__ACCOUNTING_CUSTOMER_PAYMENTS = 'BROKER__ACCOUNTING_CUSTOMER_PAYMENTS',
  BROKER__ACCOUNTING_PAYMENT_HISTORY = 'BROKER__ACCOUNTING_PAYMENT_HISTORY',

  BROKER__SETTINGS_CHECK_CALLS = 'BROKER__SETTINGS_CHECK_CALLS',
  BROKER__SETTINGS_LOAD_TASKS = 'BROKER__SETTINGS_LOAD_TASKS',
  BROKER__SETTINGS_ACCOUNT = 'BROKER__SETTINGS_ACCOUNT',
  BROKER__SETTINGS_EMAIL_MANAGEMENT = 'BROKER__SETTINGS_EMAIL_MANAGEMENT',
  BROKER__SETTINGS_USERS = 'BROKER__SETTINGS_USERS',
  BROKER__ZUUMANITY_PROFILE = 'BROKER__ZUUMANITY_PROFILE',
  BROKER__ZUUMANITY_SCOREBOARD = 'BROKER__ZUUMANITY_SCOREBOARD',
  BROKER__ZUUMANITY_MANAGEMENT = 'BROKER__ZUUMANITY_MANAGEMENT',
  SHIPPER_WEB__GET_QUOTES = 'SHIPPER_WEB__GET_QUOTES',
  SHIPPER_WEB__QUOTES_LISTING = 'SHIPPER_WEB__QUOTES_LISTING',
  SHIPPER_WEB__SHIPMENTS_LISTING = 'SHIPPER_WEB__SHIPMENTS_LISTING',
  SHIPPER_WEB__SHIPMENT_DETAILS_PAGE = 'SHIPPER_WEB__SHIPMENT_DETAILS_PAGE',
  SHIPPER_WEB__SHIPMENT_DETAILS_TAB = 'SHIPPER_WEB__SHIPMENT_DETAILS_TAB',
  SHIPPER_WEB__SHIPMENT_TRACKING_TAB = 'SHIPPER_WEB__SHIPMENT_TRACKING_TAB',
  SHIPPER_WEB__SHIPMENT_DOCUMENTS_TAB = 'SHIPPER_WEB__SHIPMENT_DOCUMENTS_TAB',
  SHIPPER_WEB__SHIPMENT_ISSUES_TAB = 'SHIPPER_WEB__SHIPMENT_ISSUES_TAB',
  SHIPPER_WEB__SHIPMENT_BILL_TAB = 'SHIPPER_WEB__SHIPMENT_BILL_TAB',
  SHIPPER_WEB__ACCOUNTING_BILLS = 'SHIPPER_WEB__ACCOUNTING_BILLS',
  SHIPPER_WEB__SETTINGS_PAYMENT_METHODS = 'SHIPPER_WEB__SETTINGS_PAYMENT_METHODS',
  SHIPPER_WEB__SETTINGS_COMPANY = 'SHIPPER_WEB__SETTINGS_COMPANY',
  SHIPPER_WEB__SETTINGS_ACCOUNT = 'SHIPPER_WEB__SETTINGS_ACCOUNT',
  SHIPPER_WEB__SETTINGS_USERS = 'SHIPPER_WEB__SETTINGS_USERS',
}
export const VIEW_ONLY_PERMISSION_MESSAGE = 'You have View Only access for this page';
